import { useEffect, useState } from "react";
import { FiArrowLeft, FiCheck, FiChevronDown, FiCopy, FiCreditCard, FiExternalLink } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import { getOpsOrderById } from "../../../../../api/adminOpsOrders";
import { ComponentLoader } from "../../../../../components/loaders";
import { OrderTransactionWrapper } from "../order-transaction.style";

const formatStatus = (value) => value ? String(value).replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()) : "Not available";
const currency = (value) => value === null || value === undefined ? "Not available" : Number(value).toLocaleString("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 });
const formatDate = (value) => value ? new Date(value).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" }) : "Not available";
const updatedByLabel = (updatedBy) => typeof updatedBy === "string"
  ? updatedBy
  : updatedBy?.name || updatedBy?.email || updatedBy?.user_id || "";
const address = (location) => [location?.street, location?.city, location?.state, location?.post_code].filter(Boolean).join(", ") || "No delivery address";
const tone = (status) => ["success", "purchased", "delivered", "user_rated", "confirmed", "approved"].includes(status) ? "success" : ["pending", "pending_payment", "pending_approval"].includes(status) ? "pending" : "info";
const yesNo = (value) => value === null || value === undefined ? "Not available" : value ? "Yes" : "No";
const display = (value) => value === null || value === undefined || value === "" ? "Not available" : String(value);
const percentage = (value) => value === null || value === undefined ? "Not available" : `${(Number(value) * 100).toFixed(1)}%`;

const DetailItem = ({ label, children, wide = false, copyValue }) => {
  const [copied, setCopied] = useState(false);
  const canCopy = copyValue !== null && copyValue !== undefined && copyValue !== "";

  const copyToClipboard = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(String(copyValue));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={`info-item${wide ? " wide" : ""}`}>
      <span>{label}</span>
      <div className="info-value">
        <strong>{children}</strong>
        {canCopy && <button type="button" className={`copy-button${copied ? " copied" : ""}`} onClick={copyToClipboard} aria-label={copied ? `${label} copied` : `Copy ${label}`} title={copied ? "Copied" : `Copy ${label}`}>{copied ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}</button>}
      </div>
    </div>
  );
};

const AccordionCard = ({ title, description, headerAction, children, className = "", defaultMobileOpen = false }) => {
  const [mobileOpen, setMobileOpen] = useState(defaultMobileOpen);
  const toggleFromHeader = (event) => {
    if (typeof window === "undefined" || !window.matchMedia("(max-width: 620px)").matches) return;
    if (event.target.closest("a, button, input, select, textarea")) return;
    setMobileOpen((current) => !current);
  };

  return (
    <section className={`content-card mobile-accordion ${mobileOpen ? "mobile-open" : ""} ${className}`.trim()}>
      <div className="card-heading" onClick={toggleFromHeader}>
        <div><h2>{title}</h2><p>{description}</p></div>
        <div className="accordion-heading-actions">
          {headerAction}
          <button type="button" className="mobile-accordion-toggle" onClick={() => setMobileOpen((current) => !current)} aria-expanded={mobileOpen} aria-label={`${mobileOpen ? "Collapse" : "Expand"} ${title}`}>
            <FiChevronDown aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="mobile-accordion-body">{children}</div>
    </section>
  );
};

const customerPath = (userId, userType) => userId ? `/admin/internal/customers/${String(userType || "").toLowerCase().includes("guest") ? "guests" : "registered"}/${userId}` : null;

const TransactionDetails = ({ transaction, index, fallbackProfilePath }) => {
  const metadata = transaction.metadata || {};
  const transactionProfilePath = customerPath(transaction.user || metadata.buyer_id, transaction.user_type) || fallbackProfilePath;
  const splitBuyItems = Array.isArray(metadata.split_buy_id)
    ? metadata.split_buy_id
    : metadata.split_buy_id
      ? [{ split_buy_id: metadata.split_buy_id }]
      : [];

  return (
    <article className="transaction-record">
      <div className="transaction-record-head">
        <div><span className="record-overline">Transaction {index + 1}</span><h3>{display(transaction.reference)}</h3></div>
        <span className={`status-pill ${tone(transaction.status)}`}>{formatStatus(transaction.status)}</span>
      </div>

      <div className="detail-section">
        <h4>Transaction record</h4>
        <div className="info-grid compact">
          <DetailItem label="Transaction ID" copyValue={transaction._id}>{display(transaction._id)}</DetailItem>
          <DetailItem label="Reference" copyValue={transaction.reference}>{display(transaction.reference)}</DetailItem>
          <DetailItem label="User ID" copyValue={transaction.user}>{transactionProfilePath && transaction.user ? <Link to={transactionProfilePath}>{display(transaction.user)}</Link> : display(transaction.user)}</DetailItem>
          <DetailItem label="User type">{display(transaction.user_type)}</DetailItem>
          <DetailItem label="Product ID" copyValue={transaction.product}>{display(transaction.product)}</DetailItem>
          <DetailItem label="Purpose">{formatStatus(transaction.purpose)}</DetailItem>
          <DetailItem label="Amount">{currency(transaction.amount)}</DetailItem>
          <DetailItem label="Buy-in amount">{currency(transaction.buy_in_amount)}</DetailItem>
          <DetailItem label="Transaction fee">{currency(transaction.transaction_fee)}</DetailItem>
          <DetailItem label="Delivery fee">{currency(transaction.delivery_fee)}</DetailItem>
          <DetailItem label="Payment verified">{yesNo(transaction.payment_verified)}</DetailItem>
          <DetailItem label="Refundable percentage">{percentage(transaction.refundable_percentage)}</DetailItem>
          <DetailItem label="Created">{formatDate(transaction.createdAt)}</DetailItem>
          <DetailItem label="Last updated">{formatDate(transaction.updatedAt)}</DetailItem>
          <DetailItem label="Record version">{display(transaction.__v)}</DetailItem>
          <DetailItem label="Payment authorization" wide>
            {transaction.payment_authorization_url ? <a href={transaction.payment_authorization_url} target="_blank" rel="noreferrer">Open payment page <FiExternalLink /></a> : "Not available"}
          </DetailItem>
        </div>
      </div>

      <div className="detail-section metadata-section">
        <h4>Payment metadata</h4>
        <div className="info-grid compact">
          <DetailItem label="Buyer ID" copyValue={metadata.buyer_id}>{transactionProfilePath && metadata.buyer_id ? <Link to={transactionProfilePath}>{display(metadata.buyer_id)}</Link> : display(metadata.buyer_id)}</DetailItem>
          <DetailItem label="Email">{transactionProfilePath && metadata.email ? <Link to={transactionProfilePath}>{metadata.email}</Link> : display(metadata.email)}</DetailItem>
          <DetailItem label="Metadata reference" copyValue={metadata.reference}>{display(metadata.reference)}</DetailItem>
          <DetailItem label="Combined delivery">{yesNo(metadata.is_combined_delivery)}</DetailItem>
          <DetailItem label="Payment amount">{currency(metadata.payment_amount)}</DetailItem>
          <DetailItem label="Balance amount">{currency(metadata.balance_amount)}</DetailItem>
          <DetailItem label="Delivery fee">{currency(metadata.delivery_fee)}</DetailItem>
          <DetailItem label="Transaction fee">{currency(metadata.transaction_fee)}</DetailItem>
          <DetailItem label="Slots bought">{display(metadata.fractional_slot_bought)}</DetailItem>
          <DetailItem label="Slot amount">{currency(metadata.slot_amount)}</DetailItem>
          <DetailItem label="Additional slots">{display(metadata.additional_slot)}</DetailItem>
          <DetailItem label="Additional-slot payment">{yesNo(metadata.is_additional_slot)}</DetailItem>
        </div>

        <div className="metadata-deals">
          <h4>Split-Buy metadata</h4>
          {splitBuyItems.length ? splitBuyItems.map((item, itemIndex) => (
            <div className="metadata-deal" key={`${item.split_buy_id || "split-buy"}-${itemIndex}`}>
              {item.split_buy_image_url && <img src={item.split_buy_image_url} alt="" />}
              <div className="info-grid compact">
                <DetailItem label="Split-Buy ID" copyValue={item.split_buy_id}>{display(item.split_buy_id)}</DetailItem>
                <DetailItem label="Split-Buy name">{display(item.split_buy_name)}</DetailItem>
                <DetailItem label="Product ID" copyValue={item.product_id}>{display(item.product_id)}</DetailItem>
                <DetailItem label="Slots purchased">{display(item.fractional_number_of_slots_purchased)}</DetailItem>
                <DetailItem label="Buy-in amount">{currency(item.buy_in_amount)}</DetailItem>
                <DetailItem label="Delivery fee">{currency(item.delivery_fee)}</DetailItem>
                <DetailItem label="Transaction fee">{currency(item.transaction_fee)}</DetailItem>
                <DetailItem label="Payment status">{formatStatus(item.payment_status)}</DetailItem>
                <DetailItem label="Delivery choices" wide>{Array.isArray(item.choice_of_delivery_locations) ? item.choice_of_delivery_locations.join(", ") || "Not available" : display(item.choice_of_delivery_locations)}</DetailItem>
              </div>
            </div>
          )) : <p className="empty-copy">No Split-Buy metadata was supplied.</p>}
        </div>
      </div>
    </article>
  );
};

export const OrderTransactionSection = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getOpsOrderById(orderId).then((response) => {
      setOrder(response.data);
    }).catch((requestError) => setError(requestError?.message || "The transaction could not be loaded."))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) return <OrderTransactionWrapper><ComponentLoader /></OrderTransactionWrapper>;
  if (!order) return <OrderTransactionWrapper><Link className="back-link" to="/admin/internal/operations/orders"><FiArrowLeft /> Back to orders</Link><section className="content-card not-found"><h1>Transaction not found</h1><p>{error || "The selected order does not exist in the currently fetched operations data."}</p></section></OrderTransactionWrapper>;

  const transactions = Array.isArray(order.transactions) ? order.transactions : [];
  const splitBuy = order.split_buy || {};
  const orderUserType = order.user_type || transactions[0]?.user_type;
  const customerDirectory = String(orderUserType || "").toLowerCase().includes("guest") ? "guests" : "registered";
  const customerProfilePath = `/admin/internal/customers/${customerDirectory}/${order.user_id}`;

  return (
    <OrderTransactionWrapper>
      <Link className="back-link" to="/admin/internal/operations/orders"><FiArrowLeft /> Back to orders &amp; fulfilment</Link>
      <header className="transaction-header">
        <div className="transaction-title"><span className="transaction-icon"><FiCreditCard /></span><div><div className="overline">Order transaction</div><h1>#{order.order_id.slice(-8).toUpperCase()}</h1><p>Placed {formatDate(order.created_at)} · <Link to={customerProfilePath}>{order.contact_name || order.email || order.user_id}</Link></p></div></div>
        <div className="header-status"><span className={`status-pill ${tone(order.payment_status)}`}>Payment {formatStatus(order.payment_status)}</span><span className={`status-pill ${tone(order.fulfillment_status)}`}>{formatStatus(order.fulfillment_status)}</span></div>
      </header>

      <div className="detail-grid">
        <main className="stack">
          <AccordionCard title="Customer and delivery" description="Every customer and destination field supplied with this order." headerAction={<Link className="customer-profile-link" to={customerProfilePath}>View user profile <FiExternalLink /></Link>}>
            <div className="section-content"><div className="info-grid">
              <DetailItem label="Customer"><Link className="customer-name-link" to={customerProfilePath}>{display(order.contact_name)} <FiExternalLink /></Link></DetailItem>
              <DetailItem label="User ID" copyValue={order.user_id}><Link to={customerProfilePath}>{display(order.user_id)}</Link></DetailItem>
              <DetailItem label="Email">{order.email ? <Link to={customerProfilePath}>{order.email}</Link> : "Not available"}</DetailItem>
              <DetailItem label="Phone">{order.contact_phone ? <a href={`tel:${order.contact_phone}`}>{order.contact_phone}</a> : "Not available"}</DetailItem>
              <DetailItem label="Delivery address" wide>{address(order.delivery_location)}</DetailItem>
              <DetailItem label="Latitude">{display(order.delivery_location?.latitude)}</DetailItem>
              <DetailItem label="Longitude">{display(order.delivery_location?.longitude)}</DetailItem>
            </div></div>
          </AccordionCard>

          <AccordionCard title="Order summary" description="Payment, membership, quantity and fulfilment values for the order." headerAction={<span className={`status-pill ${tone(order.payment_status)}`}>{formatStatus(order.payment_status)}</span>}>
            <div className="section-content"><div className="info-grid compact">
              <DetailItem label="Order ID">{display(order.order_id)}</DetailItem>
              <DetailItem label="Created">{formatDate(order.created_at)}</DetailItem>
              <DetailItem label="Slots purchased">{display(order.fractional_number_of_slots_purchased)}</DetailItem>
              <DetailItem label="Total amount paid">{currency(order.total_amount_paid)}</DetailItem>
              <DetailItem label="Payment status">{formatStatus(order.payment_status)}</DetailItem>
              <DetailItem label="Membership status">{formatStatus(order.membership_status)}</DetailItem>
              <DetailItem label="Fulfilment status">{formatStatus(order.fulfillment_status)}</DetailItem>
              <DetailItem label="Transactions">{transactions.length}</DetailItem>
            </div></div>
          </AccordionCard>

          <AccordionCard title="Transaction details" description="Complete transaction records and payment metadata returned with this order." headerAction={<span className="record-count">{transactions.length}</span>} className="transactions-card">
            <div className="section-content transaction-list">
              {transactions.length ? transactions.map((transaction, index) => <TransactionDetails transaction={transaction} index={index} fallbackProfilePath={customerProfilePath} key={transaction._id || transaction.reference || index} />) : <p className="empty-copy">No transaction record was supplied for this order.</p>}
            </div>
          </AccordionCard>

          <AccordionCard title="Parent Split-Buy" description="Complete campaign information attached to this order.">
            <div className="section-content">
              <Link className="split-buy-link" to={`/admin/internal/resources/split-buy/${splitBuy.split_buy_id}`}><img className="product-thumb" src={splitBuy.product_picture} alt="" /><div><span className="primary-cell">{display(splitBuy.product_title)}</span><span className="secondary-cell">#{splitBuy.split_buy_id?.slice(-8).toUpperCase() || "Not available"}</span></div><FiExternalLink aria-hidden="true" /></Link>
              <div className="info-grid compact campaign-grid">
                <DetailItem label="Split-Buy ID" copyValue={splitBuy.split_buy_id}>{display(splitBuy.split_buy_id)}</DetailItem>
                <DetailItem label="Campaign status">{formatStatus(splitBuy.split_buy_status)}</DetailItem>
                <DetailItem label="Purchased">{yesNo(splitBuy.purchased_flag)}</DetailItem>
                <DetailItem label="Purchased at">{formatDate(splitBuy.purchased_at)}</DetailItem>
                <DetailItem label="Completed">{yesNo(splitBuy.completed)}</DetailItem>
                <DetailItem label="Sealed">{yesNo(splitBuy.sealed)}</DetailItem>
                <DetailItem label="Buy-ins required">{display(splitBuy.number_of_buy_ins_required)}</DetailItem>
                <DetailItem label="Buy-ins attained">{display(splitBuy.fractional_number_of_buy_ins_attained)}</DetailItem>
                <DetailItem label="Campaign total">{currency(splitBuy.total_cost_to_be_split)}</DetailItem>
                <DetailItem label="Total orders">{display(splitBuy.total_orders)}</DetailItem>
                <DetailItem label="Fulfilled orders">{display(splitBuy.fulfilled_orders_count)}</DetailItem>
                <DetailItem label="Campaign created">{formatDate(splitBuy.created_at)}</DetailItem>
              </div>
            </div>
          </AccordionCard>
        </main>

        <aside className="stack fulfilment-sidebar">
          <AccordionCard title="Fulfilment timeline" description="Status history for this order.">
            <div className="section-content"><ol className="timeline"><li><span className="timeline-dot" /><strong>Order received</strong><span>{formatDate(order.created_at)}</span></li>{order.fulfillment_history?.map((item, index) => { const updatedBy = updatedByLabel(item.updated_by); return <li key={`${item.status}-${item.updated_at}-${index}`}><span className="timeline-dot" /><strong>{formatStatus(item.status)}</strong><span>{formatDate(item.updated_at)}</span>{updatedBy && <span className="timeline-by">Updated by {updatedBy}</span>}</li>; })}</ol></div>
          </AccordionCard>
        </aside>
      </div>
    </OrderTransactionWrapper>
  );
};
